import { computed, makeObservable, observable } from 'mobx';

import { delay } from 'utils/Async';

import type AssetsManager from './Assets';

export default class AssetUploadManager {
    private readonly parent: AssetsManager;
    public modalVisible = false;
    public optimize = false;
    private _file: File | undefined = undefined;
    // The chosen file's name - or, until one is chosen, the name of the asset
    // being updated (beginUpdate), which is what the modal shows meanwhile.
    private _fileName = '';
    private _loading = false;

    constructor(parent: AssetsManager) {
        this.parent = parent;
        makeObservable<AssetUploadManager, '_file' | '_fileName' | '_loading'>(
            this,
            {
                modalVisible: observable,
                optimize: observable,
                _file: observable.ref,
                _fileName: observable,
                _loading: observable,
                isUpdate: computed,
            }
        );
    }

    public get file(): File | undefined {
        return this._file;
    }

    public set file(file: File | undefined) {
        this._file = file;
        this._fileName = file?.name ?? '';
    }

    public get fileName(): string {
        return this._fileName;
    }

    public get loading(): boolean {
        return this._loading;
    }

    // Whether the current folder already has an asset by this name. Derived
    // rather than stored, so it stays current as assets arrive or go.
    public get isUpdate(): boolean {
        if (!this._fileName) return false;
        return !!this.parent.getByNameInFolder(this._fileName);
    }

    // Prepares the modal to update the existing asset with this name, which
    // it shows until a file is chosen.
    public beginUpdate(name: string) {
        this.reset();
        this._fileName = name;
    }

    public async upload() {
        if (!this._file) return;
        this._loading = true;
        try {
            await this.parent.uploadToCurrentAssetFolder(this._file, {
                postProcess: this.optimize,
            });
        } finally {
            this._loading = false;
        }
        void this.hideModalAndReset();
    }

    public async hideModalAndReset() {
        this.modalVisible = false;
        await delay(250);
        this.reset();
    }

    private reset() {
        this._file = undefined;
        this._fileName = '';
        this._loading = false;
    }
}
